import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import AnimatedCounter from "./AnimatedCounter";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const countries = [
  "India", "United States", "United Kingdom", "United Arab Emirates", "Singapore", "Canada", "Australia", "Germany", "Other",
];
const primaryGoals = [
  "Metabolic health & weight",
  "Prevent chronic disease",
  "Improve sleep & energy",
  "Reduce stress & burnout",
  "Optimize longevity",
  "Manage a diagnosed condition",
];
const betaInterest = [
  "Casually curious",
  "Actively looking for this",
  "Would use it today",
  "Willing to pay for early access",
];
const wtp = [
  "Free tier only",
  "₹499–999 / month",
  "₹1,000–1,999 / month",
  "₹2,000+ / month",
  "Whatever it takes",
];

export default function Waitlist() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", country: "",
    primary_goal: "", biggest_challenge: "",
    beta_interest: "", willingness_to_pay: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [count, setCount] = useState(0);
  const [refCode, setRefCode] = useState("");
  const [signup, setSignup] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get("ref");
      if (ref) { setRefCode(ref); sessionStorage.setItem("hirl_ref", ref); }
      else {
        const stored = sessionStorage.getItem("hirl_ref");
        if (stored) setRefCode(stored);
      }
    } catch (_) { /* ignore */ }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/waitlist/stats`);
        if (!cancelled) setCount(data?.total ?? 0);
      } catch (_) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v?.target?.value ?? "" }));

  const submit = async (e) => {
    e.preventDefault();
    const required = ["name", "email", "country", "primary_goal", "biggest_challenge", "beta_interest", "willingness_to_pay"];
    if (required.some((k) => !form[k])) {
      toast.error("Please fill in every field marked required.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (refCode) payload.referred_by = refCode;
      const { data } = await axios.post(`${API}/waitlist`, payload);
      setSignup(data);
      setCount(data?.total ?? count + 1);
      setSuccess(true);
      sessionStorage.removeItem("hirl_ref");
      toast.success("You're on the list. We'll be in touch shortly.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const copyReferral = async () => {
    if (!signup?.referral_url) return;
    try {
      await navigator.clipboard.writeText(signup.referral_url);
      setCopied(true);
      toast.success("Link copied. Share to climb the list.");
      setTimeout(() => setCopied(false), 2500);
    } catch (_) {
      toast.error("Couldn't copy — long-press the link to copy manually.");
    }
  };

  return (
    <section id="waitlist" data-testid="waitlist-section" className="relative py-24 md:py-32">
      <div className="absolute inset-0 -z-10">
        <div className="absolute left-1/2 top-1/3 h-[520px] w-[720px] -translate-x-1/2 bg-radial-teal opacity-70" />
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <div className="rounded-3xl border border-white/[0.08] glass-strong overflow-hidden">
          <div className="grid lg:grid-cols-[1fr,1.15fr]">
            {/* Left */}
            <div className="p-8 md:p-12 border-b lg:border-b-0 lg:border-r border-white/[0.06]">
              <div className="text-[11px] uppercase tracking-[0.22em] text-teal-soft">09 — Beta access</div>
              <h2 className="mt-4 font-heading text-3xl sm:text-4xl tracking-tighter leading-[1.05] font-medium">
                Get early access to
                <br /><span className="gradient-text-teal-lav">HealthIRL.</span>
              </h2>
              <p className="mt-4 text-zinc-400">
                A small, opinionated cohort shapes the product. Early members lock in founder pricing and priority onboarding.
              </p>

              <div className="mt-8 rounded-2xl border border-white/[0.06] bg-ink-800/60 p-5">
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">On the waitlist</div>
                <div className="mt-1 font-heading text-4xl tracking-tighter font-medium text-white"><AnimatedCounter value={count} /></div>
                <div className="mt-2 text-xs text-zinc-500">people getting decoded.</div>
              </div>

              <ul className="mt-6 space-y-2.5 text-sm text-zinc-300">
                {[
                  "Private beta access",
                  "Founding-member pricing for life",
                  "Priority biomarker integrations",
                  "Direct line to the founders",
                ].map((p) => (
                  <li key={p} className="flex items-center gap-2.5">
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-teal-mid/20 text-teal-soft">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5L20 7" /></svg>
                    </span>
                    {p}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right */}
            <div className="relative p-8 md:p-12">
              <AnimatePresence mode="wait">
                {success ? (
                  <motion.div key="success"
                    initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className="h-full flex flex-col items-center justify-center text-center"
                    data-testid="waitlist-success">
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 180, damping: 14 }}
                      className="h-16 w-16 rounded-full bg-teal-mid/20 border border-teal-mid/40 flex items-center justify-center glow-teal">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2DD4BF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5L20 7" /></svg>
                    </motion.div>
                    <h3 className="mt-5 font-heading text-2xl tracking-tighter">You&rsquo;re on the waitlist.</h3>
                    <p className="mt-2 text-sm text-zinc-400 max-w-sm">
                      Confirmation sent to <span className="text-zinc-200">{signup?.email || form.email}</span>. We&rsquo;ll be in touch with your beta access.
                    </p>

                    {signup?.position && (
                      <div data-testid="waitlist-position" className="mt-6 inline-flex items-baseline gap-2 rounded-full border border-teal-mid/30 bg-teal-mid/[0.06] px-4 py-2">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-teal-soft">Your position</span>
                        <span className="font-heading text-2xl tracking-tighter text-white">#{signup.position}</span>
                        <span className="text-xs text-zinc-500">of {signup.total}</span>
                      </div>
                    )}

                    {signup?.referral_url && (
                      <div className="mt-6 w-full max-w-sm">
                        <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Climb the list — refer a friend</div>
                        <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-1.5">
                          <code data-testid="waitlist-referral-url" className="flex-1 text-left text-xs font-mono text-zinc-300 px-3 py-2 truncate">{signup.referral_url}</code>
                          <button onClick={copyReferral} data-testid="waitlist-copy-referral-btn"
                            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition-colors ${
                              copied ? "bg-teal-mid/25 text-teal-soft" : "bg-teal-soft text-ink-900 hover:bg-teal-mid"
                            }`}>
                            {copied ? (
                              <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5L20 7" /></svg>Copied</>
                            ) : (
                              <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg>Copy link</>
                            )}
                          </button>
                        </div>
                        <div className="mt-2 text-[11px] text-zinc-500">Each friend who joins moves you up 10 spots.</div>
                      </div>
                    )}

                    <button
                      onClick={() => {
                        setSuccess(false); setSignup(null);
                        setForm({ name: "", email: "", phone: "", country: "", primary_goal: "", biggest_challenge: "", beta_interest: "", willingness_to_pay: "" });
                      }}
                      data-testid="waitlist-add-another-btn"
                      className="mt-6 text-xs uppercase tracking-wider text-teal-soft hover:text-teal-mid">
                      ← Refer someone else
                    </button>
                  </motion.div>
                ) : (
                  <motion.form key="form" onSubmit={submit}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="space-y-4" data-testid="waitlist-form">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Full name *">
                        <input value={form.name} onChange={setField("name")} placeholder="Mansi Mehta" data-testid="waitlist-input-name"
                          className="w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-teal-mid/60 focus:ring-2 focus:ring-teal-mid/25 transition" />
                      </Field>
                      <Field label="Email *">
                        <input type="email" value={form.email} onChange={setField("email")} placeholder="you@domain.com" data-testid="waitlist-input-email"
                          className="w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-teal-mid/60 focus:ring-2 focus:ring-teal-mid/25 transition" />
                      </Field>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Phone (optional)">
                        <input value={form.phone} onChange={setField("phone")} placeholder="+91 …" data-testid="waitlist-input-phone"
                          className="w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-teal-mid/60 focus:ring-2 focus:ring-teal-mid/25 transition" />
                      </Field>
                      <Field label="Country *">
                        <Select value={form.country} onValueChange={setField("country")}>
                          <SelectTrigger data-testid="waitlist-select-country" className="bg-white/[0.03] border-white/10 text-white h-12 rounded-xl focus:ring-teal-mid/30 focus:border-teal-mid/60">
                            <SelectValue placeholder="Where are you based?" />
                          </SelectTrigger>
                          <SelectContent className="bg-ink-800 border-white/10 text-white">
                            {countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </Field>
                    </div>

                    <Field label="Primary health goal *">
                      <Select value={form.primary_goal} onValueChange={setField("primary_goal")}>
                        <SelectTrigger data-testid="waitlist-select-goal" className="bg-white/[0.03] border-white/10 text-white h-12 rounded-xl focus:ring-teal-mid/30 focus:border-teal-mid/60">
                          <SelectValue placeholder="What matters most right now?" />
                        </SelectTrigger>
                        <SelectContent className="bg-ink-800 border-white/10 text-white">
                          {primaryGoals.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>

                    <Field label="Biggest challenge with your health *">
                      <textarea value={form.biggest_challenge} onChange={setField("biggest_challenge")} data-testid="waitlist-input-challenge" rows={2}
                        placeholder="In one line — what have you been stuck on?"
                        className="w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-teal-mid/60 focus:ring-2 focus:ring-teal-mid/25 transition resize-none" />
                    </Field>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Beta interest *">
                        <Select value={form.beta_interest} onValueChange={setField("beta_interest")}>
                          <SelectTrigger data-testid="waitlist-select-interest" className="bg-white/[0.03] border-white/10 text-white h-12 rounded-xl focus:ring-teal-mid/30 focus:border-teal-mid/60">
                            <SelectValue placeholder="How keen are you?" />
                          </SelectTrigger>
                          <SelectContent className="bg-ink-800 border-white/10 text-white">
                            {betaInterest.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label="Willingness to pay *">
                        <Select value={form.willingness_to_pay} onValueChange={setField("willingness_to_pay")}>
                          <SelectTrigger data-testid="waitlist-select-wtp" className="bg-white/[0.03] border-white/10 text-white h-12 rounded-xl focus:ring-teal-mid/30 focus:border-teal-mid/60">
                            <SelectValue placeholder="What feels fair?" />
                          </SelectTrigger>
                          <SelectContent className="bg-ink-800 border-white/10 text-white">
                            {wtp.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </Field>
                    </div>

                    <button type="submit" disabled={loading} data-testid="waitlist-submit-btn"
                      className="group w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-medium text-ink-900 bg-teal-soft hover:bg-teal-mid disabled:opacity-60 disabled:cursor-not-allowed transition-all glow-teal">
                      {loading ? (
                        <><svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M21 12a9 9 0 1 1-6.2-8.55"/></svg>Reserving your seat…</>
                      ) : (
                        <>Reserve my seat
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="group-hover:translate-x-0.5 transition-transform"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
                        </>
                      )}
                    </button>
                    <p className="text-[11px] text-zinc-500">We email rarely. No spam. Unsubscribe with one click.</p>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
